import { Injectable } from '@angular/core';
import { CardTokenStore } from './card-token.store';
import { CardToken } from './models/card-token.model';
import { tap, catchError, switchMap, map, mapTo } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';
import { of, Observable, firstValueFrom } from 'rxjs';

import { CardTokenQuery } from './card-token.query';
import { LoggingService } from '../../shared/services/core/logging.service';
import { ProviderName } from '../../account/models/login-response.model';
import { PaymentService } from '../../shared/services/payment/payment.service';
import { withTransaction, applyTransaction } from '@datorama/akita';

@Injectable({ providedIn: 'root' })

//FIXME: needs to be refactored along with API to use TillTransactionAPI to check for card token.
export class CardTokenService {
  public turnOn: boolean;
  constructor(
    private cardTokenStore: CardTokenStore,
    private cardTokenQuery: CardTokenQuery,
    private api: ApiService,
    private logService: LoggingService,
    private paymentService: PaymentService
  ) {}

  /** Fetches the payment card tokens and puts them in the store. */
  fetchCardTokens(ignoreCache: boolean = false): Observable<null> {
    const request$: Observable<null> = this.api.get<CardToken>('cardtokens').pipe(
      tap((cardTokens) => this.cardTokenStore.set(cardTokens)),
      mapTo(null)
    );

    if (ignoreCache) {
      return request$;
    }

    return this.cardTokenQuery.getHasCache() ? of(null) : request$;
  }

  /** Fetches the default payment card token, puts it in store and marks it as the active card token. */
  fetchDefaultCardToken(): Observable<null> {
    return this.api.getAnySingle<CardToken>('cardtokens/default').pipe(
      tap((cardToken) => {
        if (cardToken) {
          applyTransaction(() => {
            this.cardTokenStore.upsert(cardToken.TokenId, cardToken);
            this.cardTokenStore.setActive(cardToken.TokenId);
          });
        } else {
          this.cardTokenStore.removeActive(this.cardTokenQuery.getActiveId());
        }
      }),
      mapTo(null)
    );
  }

  /** Makes a request to add a new payment card token. */
  addNewCardToken(
    provider: ProviderName,
    redirectUrl: string,
    formName: string = 'topUpForm',
    failureUrl: string = '/ecashfailure'
  ): Promise<any> {
    this.logService.debug('Adding a new card token', { provider, redirectUrl, formName, failureUrl });
    return firstValueFrom(this.api
      .post('cardtokens/cardtokens', { provider, redirectUrl, failureUrl })
      .pipe(
        tap(() => {
          this.fetchCardTokens(true);
          this.fetchDefaultCardToken();
        })
      ))
      .then((response) => {
        // Adding a card token creates no order, so nothing has populated the payment request in the
        // store. Pass redirectUrl through as the callback url or the rendered payment component has
        // nowhere to navigate once the provider returns a token.
        this.paymentService.process(response, formName, failureUrl, redirectUrl);
        return response;
      });
  }

  /** Sets a given payment card token ID as the primary card token. */
  setCardAsDefault(cardTokenId: number): void {
    this.logService.debug('Setting card token as default', cardTokenId);
    this.api
      .put('cardtokens/default', cardTokenId, '')
      .pipe(
        withTransaction(() => {
          this.cardTokenStore.setActive(cardTokenId);
          this.cardTokenStore.update(null, { IsDefaultToken: false, Frozen: false });
          this.cardTokenStore.update(cardTokenId, { IsDefaultToken: true });
        }),
        mapTo(null),
        catchError((error) => {
          this.logService.error('Failed to set card as primary.', error);
          throw error;
        })
      )
      .toPromise();
  }

  /** Freezes a given payment card token by ID. */
  setCardTokenFrozenState(cardTokenId: number, frozen: boolean): void {
    this.logService.debug('Set card token frozen state to ' + frozen, cardTokenId);
    this.api
      .put('cardtokens/' + cardTokenId + '/freeze', frozen ? 1 : 0, '')
      .pipe(
        switchMap(() => this.cardTokenQuery.selectCard()),
        // Change the matching card token to be frozen in the store
        map((cardTokens) => {
          let updatedCardTokens: CardToken[] = JSON.parse(JSON.stringify(cardTokens));
          return updatedCardTokens.filter((cardToken) => {
            if (cardToken.TokenId === cardTokenId) {
              cardToken.Frozen = frozen;
            }
            return cardToken;
          });
        }),
        tap((cardTokens) => this.cardTokenStore.set(cardTokens)),
        mapTo(null)
      )
      .toPromise();
  }

  /** Deletes a given payment card token by ID. */
  deleteCardToken(cardTokenId: number): void {
    this.logService.debug('Deleting card token', cardTokenId);
    this.api
      .delete('cardtokens', cardTokenId)
      .pipe(
        tap(() => this.cardTokenStore.remove(cardTokenId)),
        mapTo(null)
      )
      .toPromise();
  }

  setCardTokenStatus(status: boolean): Promise<null> {
    this.logService.debug(`Setting card token cashback status to ${status}`);
    return firstValueFrom(
      this.api.post(`cardtokens/ecash/status/${status}`, {}).pipe(
        mapTo(null),
        catchError((error) => {
          this.logService.error(`Failed to set card token cashback status as ${status}`, error);
          throw error;
        })
      )
    );
  }

  fetchCardTokenStatus(): Observable<boolean> {
    this.logService.debug('Getting card token cashback status');
    const request$: Observable<boolean> = this.api.getAnySingle<boolean>('cardtokens/ecash/status');

    return request$;
  }
}
